import { MutableRefObject, useEffect, useRef } from 'react';
import { GridApi, GridScrollParams, useGridApiRef } from '@mui/x-data-grid';

interface UseGridInfiniteScrollOptions {
  apiRef: ReturnType<typeof useGridApiRef<GridApi>>;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  loading?: boolean;
  scrollThreshold?: number;
}

const useGridInfiniteScroll = ({
  apiRef,
  hasNextPage,
  onLoadMore,
  loading,
  scrollThreshold = 100,
}: UseGridInfiniteScrollOptions): MutableRefObject<boolean> => {
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    if (!hasNextPage || !onLoadMore) {
      return undefined;
    }

    return apiRef.current.subscribeEvent('scrollPositionChange', (params: GridScrollParams) => {
      const virtualScroller = apiRef.current.rootElementRef?.current?.querySelector('.MuiDataGrid-virtualScroller');

      if (!virtualScroller) {
        return;
      }

      const distanceToBottom = virtualScroller.scrollHeight - virtualScroller.clientHeight - params.top;

      if (distanceToBottom > scrollThreshold || isLoadingMoreRef.current || loading) {
        return;
      }

      isLoadingMoreRef.current = true;
      onLoadMore();
    });
  }, [apiRef, hasNextPage, onLoadMore, loading, scrollThreshold]);

  return isLoadingMoreRef;
};

export default useGridInfiniteScroll;
